/**
 * One typed reader for a video model's registry capabilities.
 *
 * The registry hands back a raw `Record<string, unknown>` for
 * `constraints.video.capabilities` (see `ModelConstraintsResponse` in
 * `structs/model.ts`). Every UI surface that needs to know what a video model
 * can take as input — first/last frame images, reference images/videos/audios,
 * and the constraints that come with them (mutually-exclusive reference
 * kinds, a fixed clip duration, allowed aspect ratios, person-generation
 * policy, how references are addressed in the prompt) — parses that same raw
 * object through {@link parseVideoCapabilities} rather than poking at
 * individual keys inline. A payload missing every new key (today's registry)
 * parses to the all-off/all-default shape below, so this is a pure refactor:
 * no consumer's derived behavior changes until the registry starts sending
 * the new keys.
 */

/** The four kinds of reference input a video generation can take. */
export type ReferenceKind =
  | 'frame_images'
  | 'reference_images'
  | 'reference_videos'
  | 'reference_audios';

export interface VideoCapabilities {
  frames: { supported: boolean; positions: Array<'first' | 'last'> };
  referenceImages: number;
  referenceVideos: number;
  referenceAudios: number;
  constraints: {
    exclusions: Array<[ReferenceKind, ReferenceKind]>;
    fixedDurationSeconds: number | null;
    aspectRatios: string[] | null;
    personGeneration: string | null;
    addressing: 'none' | 'positional' | 'elements';
  };
}

const VALID_POSITIONS = new Set(['first', 'last']);
const VALID_KINDS = new Set<string>([
  'frame_images',
  'reference_images',
  'reference_videos',
  'reference_audios',
]);
const VALID_ADDRESSING = new Set(['none', 'positional', 'elements']);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** `true` only for the literal boolean `true` — anything else is "unknown". */
const isTrue = (value: unknown): boolean => value === true;

/** A finite, positive integer count, or 0 for anything malformed/absent. */
const positiveIntOrZero = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;

const isReferenceKind = (value: unknown): value is ReferenceKind =>
  typeof value === 'string' && VALID_KINDS.has(value);

const parsePositions = (value: unknown): Array<'first' | 'last'> =>
  Array.isArray(value)
    ? value.filter((entry): entry is 'first' | 'last' =>
        typeof entry === 'string' && VALID_POSITIONS.has(entry)
      )
    : [];

const parseExclusions = (value: unknown): Array<[ReferenceKind, ReferenceKind]> =>
  Array.isArray(value)
    ? value.filter((pair): pair is [ReferenceKind, ReferenceKind] =>
        Array.isArray(pair) &&
        pair.length === 2 &&
        isReferenceKind(pair[0]) &&
        isReferenceKind(pair[1])
      )
    : [];

const parseAspectRatios = (value: unknown): string[] | null =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : null;

const parseAddressing = (value: unknown): VideoCapabilities['constraints']['addressing'] =>
  typeof value === 'string' && VALID_ADDRESSING.has(value)
    ? (value as VideoCapabilities['constraints']['addressing'])
    : 'none';

/**
 * Parses a video model's raw registry capabilities into the typed shape every
 * consumer reads. Defensive throughout: a missing, wrongly-typed, or unknown
 * value for any key falls back to that field's default rather than throwing
 * or propagating garbage (unknown frame positions / exclusion kinds are
 * dropped, an unknown `addressing` becomes `'none'`).
 */
export function parseVideoCapabilities(caps: Record<string, unknown>): VideoCapabilities {
  const safe = isPlainObject(caps) ? caps : {};
  const referenceConstraints = isPlainObject(safe.reference_constraints)
    ? safe.reference_constraints
    : {};

  return {
    frames: {
      supported: isTrue(safe.supports_frame_images),
      positions: parsePositions(safe.supported_frame_positions),
    },
    referenceImages: isTrue(safe.supports_reference_images)
      ? positiveIntOrZero(safe.max_reference_images)
      : 0,
    referenceVideos: isTrue(safe.supports_reference_videos)
      ? positiveIntOrZero(safe.max_reference_videos)
      : 0,
    referenceAudios: isTrue(safe.supports_reference_audios)
      ? positiveIntOrZero(safe.max_reference_audios)
      : 0,
    constraints: {
      exclusions: parseExclusions(referenceConstraints.exclusions),
      fixedDurationSeconds:
        typeof referenceConstraints.fixed_duration_seconds === 'number' &&
        Number.isFinite(referenceConstraints.fixed_duration_seconds)
          ? referenceConstraints.fixed_duration_seconds
          : null,
      aspectRatios: parseAspectRatios(referenceConstraints.aspect_ratios),
      personGeneration:
        typeof referenceConstraints.person_generation === 'string'
          ? referenceConstraints.person_generation
          : null,
      addressing: parseAddressing(referenceConstraints.addressing),
    },
  };
}

/**
 * The all-off/all-default shape — what a model whose capabilities are not (or
 * not yet) known parses to.
 *
 * Shared as a CONSTANT rather than re-derived per call site: several surfaces
 * hold it as the initial value of a `useState`, and a fresh object each render
 * makes every identity comparison downstream of that state false.
 */
export const UNKNOWN_VIDEO_CAPABILITIES: VideoCapabilities = parseVideoCapabilities({});

/** True when the model both supports frame images and lists "last" among its
 *  positions — the one derived boolean several frame surfaces read. */
export function supportsLastFrameOf(caps: VideoCapabilities): boolean {
  return caps.frames.supported && caps.frames.positions.includes('last');
}

/** Whether two reference kinds are declared mutually exclusive. Symmetric. */
export function excluded(caps: VideoCapabilities, a: ReferenceKind, b: ReferenceKind): boolean {
  return caps.constraints.exclusions.some(
    ([x, y]) => (x === a && y === b) || (x === b && y === a)
  );
}
